<template>
  <div class="question-config">
    <!-- 左侧产品树 -->
    <el-aside width="200px" class="product-tree-aside">
      <div class="product-tree-header">
        <h4>产品名称</h4>
      </div>
      <el-tree
        :data="productTreeData"
        :props="treeProps"
        node-key="id"
        :default-expand-all="true"
        :highlight-current="true"
        @node-click="handleProductSelect"
        class="product-tree"
      >
        <span class="custom-tree-node" slot-scope="{ node }">
          <span>{{ node.label }}</span>
        </span>
      </el-tree>
    </el-aside>

    <!-- 主内容区 -->
    <el-main class="main-content">
      <!-- 搜索工具栏 -->
      <div class="toolbar">
        <div class="search-group">
          <label>自定义检索：</label>
          <el-select 
            v-model="searchParams.type" 
            placeholder="请选择检索类型"
            style="width: 130px; margin-right: 10px;"
            @change="handleSearchTypeChange"
          >
            <el-option label="按状态" :value="1" />
            <el-option label="按发布时间" :value="2" />
            <el-option label="按问答类型" :value="3" />
          </el-select>

          <!-- 按状态搜索 -->
          <el-select 
            v-if="searchParams.type === 1"
            v-model="searchParams.disabled"
            placeholder="请选择发布状态"
            style="width: 120px; margin-right: 10px;"
          >
            <el-option label="启用" :value="false" />
            <el-option label="禁用" :value="true" />
          </el-select>

          <!-- 按时间搜索 -->
          <template v-if="searchParams.type === 2">
            <el-date-picker
              v-model="searchParams.beginTime"
              type="date"
              placeholder="开始时间"
              style="width: 140px; margin-right: 10px;"
              value-format="yyyy-MM-dd"
            />
            <el-date-picker
              v-model="searchParams.endTime"
              type="date"
              placeholder="结束时间"
              style="width: 140px; margin-right: 10px;"
              value-format="yyyy-MM-dd"
            />
          </template>

          <!-- 按问答类型搜索 -->
          <el-select 
            v-if="searchParams.type === 3"
            v-model="searchParams.faqType"
            placeholder="请选择问答类型"
            style="width: 120px; margin-right: 10px;"
          >
            <el-option label="h5客户" :value="1" />
            <el-option label="代理人" :value="2" />
            <el-option label="在线咨询" :value="4" />
          </el-select>

          <el-button type="primary" @click="handleSearch">搜索</el-button>
          <el-button @click="handleReset">全部</el-button>
        </div>
        
        <el-button type="primary" icon="el-icon-plus" @click="handleAdd" class="add-btn">
          添加
        </el-button>
      </div>

      <!-- 数据表格 -->
      <el-table 
        :data="tableData" 
        :height="tableHeight"
        v-loading="loading"
        border
        stripe
        :header-cell-style="{ backgroundColor: '#f5f7fa', color: '#606266' }"
        style="width: 100%;"
      >
        <el-table-column prop="question" label="问题" min-width="160" align="center" show-overflow-tooltip />
        
        <el-table-column prop="answer" label="回答" min-width="300" align="center" show-overflow-tooltip />
        
        <el-table-column prop="faq_type" label="问答类型" min-width="100" align="center">
          <template #default="{ row }">
            {{ formatFaqType(row.faq_type) }}
          </template>
        </el-table-column>
        
        <el-table-column prop="disabled" label="状态" min-width="80" align="center">
          <template #default="{ row }">
            <el-tag :type="row.disabled ? 'danger' : 'success'">
              {{ row.disabled ? '禁用' : '启用' }}
            </el-tag>
          </template>
        </el-table-column>
        
        <el-table-column prop="created_at" label="创建时间" min-width="180" align="center">
          <template #default="{ row }">
            {{ formatDate(row.created_at) }}
          </template>
        </el-table-column>
        
        <el-table-column prop="updated_at" label="更新时间" min-width="180" align="center">
          <template #default="{ row }">
            {{ formatDate(row.updated_at) }}
          </template>
        </el-table-column>
        
        <el-table-column prop="operator" label="操作人" min-width="100" align="center" />
        
        <el-table-column prop="note" label="备注" min-width="150" align="center" show-overflow-tooltip />
        
        <el-table-column label="操作" width="260" align="center">
          <template #default="{ row, $index }">
            <el-button type="text" size="small" @click="handleMoveUp(row, $index)" :disabled="$index === 0">
              上移
            </el-button>
            <el-button type="text" size="small" @click="handleView(row)">查看</el-button>
            <el-button type="text" size="small" @click="handleEdit(row)" v-if="row.disabled">编辑</el-button>
            <el-button 
              type="text" 
              size="small" 
              @click="handleToggleStatus(row)"
            >
              {{ row.disabled ? '启用' : '禁用' }}
            </el-button>
            <el-button 
              type="text" 
              size="small" 
              @click="handleDelete(row)"
              v-if="row.disabled"
            >
              删除
            </el-button>
            <el-button type="text" size="small" @click="handleMoveDown(row, $index)" :disabled="$index === tableData.length - 1">
              下移
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination">
        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="pagination.page"
          :page-sizes="[10, 15, 20, 25]"
          :page-size="pagination.rows"
          :total="pagination.total"
          layout="total, sizes, prev, pager, next, jumper"
        />
      </div>
    </el-main>

    <!-- 问答编辑对话框 -->
    <question-edit-dialog
      v-if="showEditDialog"
      :visible.sync="showEditDialog"
      :question-data="currentQuestion"
      :product-id="currentProductId"
      :is-view="isViewMode"
      @save="handleSaveQuestion"
      @cancel="handleCancelEdit"
    />
  </div>
</template>

<script>
import QuestionEditDialog from './components/QuestionEditDialog.vue'
import { 
  getQuestionList, 
  deleteQuestion, 
  toggleQuestionStatus,
  sortQuestion,
  getProductList
} from '@/api/product'

export default {
  name: 'QuestionConfig',
  components: {
    QuestionEditDialog
  },
  data() {
    return {
      loading: false,
      tableData: [],
      productTreeData: [],
      currentProductId: 2, // 默认产品ID，与原项目保持一致
      searchParams: {
        type: 1,
        disabled: null,
        beginTime: '',
        endTime: '',
        faqType: null
      },
      pagination: {
        page: 1,
        rows: 15,
        total: 0
      },
      showEditDialog: false,
      currentQuestion: null,
      isViewMode: false,
      tableHeight: 600,
      treeProps: {
        children: 'children',
        label: 'name'
      }
    }
  },
  mounted() {
    this.calculateTableHeight()
    this.loadProductTree()
    
    // 监听窗口大小变化
    window.addEventListener('resize', this.calculateTableHeight)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.calculateTableHeight)
  },
  methods: {
    // 计算表格高度
    calculateTableHeight() {
      this.$nextTick(() => {
        const windowHeight = window.innerHeight
        this.tableHeight = Math.max(windowHeight - 200, 400)
      })
    },

    // 加载产品树
    async loadProductTree() {
      try {
        const response = await getProductList({ disabled: false })
        if (response && response.err_code === 0) {
          this.productTreeData = response.rows || []
          
          // 默认选择第一个产品
          if (this.productTreeData.length > 0) {
            // 尝试找到id为2的产品（与原项目保持一致）
            const defaultProduct = this.productTreeData.find(item => item.id === 2) || this.productTreeData[0]
            this.currentProductId = defaultProduct.id
            this.loadData()
          }
        }
      } catch (error) {
        console.error('加载产品列表失败:', error)
        this.$message.error('加载产品列表失败')
      }
    },

    // 产品选择
    handleProductSelect(data) {
      this.currentProductId = data.id
      this.pagination.page = 1
      this.loadData()
    },

    // 加载问答数据
    async loadData() {
      if (!this.currentProductId) return
      
      this.loading = true
      try {
        const params = {
          page: this.pagination.page,
          rows: this.pagination.rows,
          product_id: this.currentProductId
        }

        // 添加搜索条件
        if (this.searchParams.type === 1 && this.searchParams.disabled !== null) {
          params.disabled = this.searchParams.disabled
        }
        if (this.searchParams.type === 2) {
          if (this.searchParams.beginTime) {
            params.begin_date = Math.floor(new Date(this.searchParams.beginTime + ' 00:00:00').getTime() / 1000)
          }
          if (this.searchParams.endTime) {
            params.end_date = Math.floor(new Date(this.searchParams.endTime + ' 23:59:59').getTime() / 1000)
          }
        }
        if (this.searchParams.type === 3 && this.searchParams.faqType !== null) {
          params.faq_type = this.searchParams.faqType
        }

        const response = await getQuestionList(params)
        if (response && response.err_code === 0) {
          this.tableData = response.rows || []
          this.pagination.total = response.total || 0
          console.log('问答数据加载成功:', this.tableData.length, '条记录')
        } else {
          this.$message.error('获取数据失败')
          console.error('接口返回错误:', response)
        }
      } catch (error) {
        console.error('加载数据失败:', error)
        this.$message.error('网络错误，请重试')
      } finally {
        this.loading = false
      }
    },

    // 搜索类型改变
    handleSearchTypeChange() {
      // 清空其他搜索条件
      this.searchParams.disabled = null
      this.searchParams.beginTime = ''
      this.searchParams.endTime = ''
      this.searchParams.faqType = null
    },

    // 搜索
    handleSearch() {
      if (this.searchParams.type === 1 && this.searchParams.disabled === null) {
        this.$message.warning('请选择发布状态')
        return
      }
      if (this.searchParams.type === 3 && this.searchParams.faqType === null) {
        this.$message.warning('请选择问答类型')
        return
      }
      
      this.pagination.page = 1
      this.loadData()
    },

    // 重置搜索
    handleReset() {
      this.searchParams = {
        type: 1,
        disabled: null,
        beginTime: '',
        endTime: '',
        faqType: null
      }
      this.pagination.page = 1
      this.loadData()
    },

    // 新增
    handleAdd() {
      if (!this.currentProductId) {
        this.$message.warning('请先选择产品再添加问答')
        return
      }
      this.currentQuestion = null
      this.isViewMode = false
      this.showEditDialog = true
    },

    // 查看
    handleView(row) {
      this.currentQuestion = { ...row }
      this.isViewMode = true
      this.showEditDialog = true
    },

    // 编辑
    handleEdit(row) {
      this.currentQuestion = { ...row }
      this.isViewMode = false
      this.showEditDialog = true
    },

    // 删除
    async handleDelete(row) {
      try {
        await this.$confirm('您确定要删除该问题吗？', '确认', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })

        const response = await deleteQuestion({ id: row.id })
        if (response && response.err_code === 0) {
          this.$message.success('删除成功')
          this.loadData()
        } else {
          this.$message.error(response?.err_msg || '删除失败')
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('删除失败:', error)
          this.$message.error('网络错误，请重试')
        }
      }
    },

    // 切换状态
    async handleToggleStatus(row) {
      const action = row.disabled ? '启用' : '禁用'
      try {
        await this.$confirm(`您确定要${action}该消息吗？`, '确认', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })

        const response = await toggleQuestionStatus({ 
          id: row.id, 
          disabled: !row.disabled 
        })
        if (response && response.err_code === 0) {
          this.$message.success(`${action}成功`)
          this.loadData()
        } else {
          this.$message.error(response?.err_msg || `${action}失败`)
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error(`${action}失败:`, error)
          this.$message.error('网络错误，请重试')
        }
      }
    },

    // 上移
    async handleMoveUp(row, index) {
      try {
        const response = await sortQuestion({ id: row.id, is_up: true })
        if (response && response.err_code === 0) {
          this.$message.success('上移成功')
          this.loadData()
        } else {
          this.$message.error(response?.err_msg || '上移失败')
        }
      } catch (error) {
        console.error('上移失败:', error)
        this.$message.error('网络错误，请重试')
      }
    },

    // 下移
    async handleMoveDown(row, index) {
      try {
        const response = await sortQuestion({ id: row.id, is_up: false })
        if (response && response.err_code === 0) {
          this.$message.success('下移成功')
          this.loadData()
        } else {
          this.$message.error(response?.err_msg || '下移失败')
        }
      } catch (error) {
        console.error('下移失败:', error)
        this.$message.error('网络错误，请重试')
      }
    },

    // 保存问答
    handleSaveQuestion() {
      this.showEditDialog = false
      this.loadData()
    },

    // 取消编辑
    handleCancelEdit() {
      this.showEditDialog = false
      this.currentQuestion = null
    },

    // 分页大小改变
    handleSizeChange(val) {
      this.pagination.rows = val
      this.pagination.page = 1
      this.loadData()
    },

    // 当前页改变
    handleCurrentChange(val) {
      this.pagination.page = val
      this.loadData()
    },

    // 格式化问答类型
    formatFaqType(type) {
      const typeMap = {
        1: 'h5客户',
        2: '代理人',
        4: '在线咨询'
      }
      return typeMap[type] || ''
    },

    // 格式化日期
    formatDate(dateStr) {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      })
    }
  }
}
</script>

<style scoped>
.question-config {
  display: flex;
  height: 100vh;
}

.product-tree-aside {
  border-right: 1px solid #e6e6e6;
  background-color: #fafafa;
}

.product-tree-header {
  padding: 20px;
  border-bottom: 1px solid #e6e6e6;
  text-align: center;
}

.product-tree-header h4 {
  margin: 0;
  font-size: 16px;
  color: #333;
}

.product-tree {
  padding: 10px;
}

.main-content {
  flex: 1;
  padding: 20px;
  overflow: hidden;
}

.toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding: 10px 0;
}

.search-group {
  display: flex;
  align-items: center;
  gap: 10px;
}

.search-group label {
  white-space: nowrap;
}

.add-btn {
  margin-left: 20px;
}

.pagination {
  margin-top: 20px;
  text-align: right;
}

/* 树节点样式 */
.custom-tree-node {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 14px;
  padding-right: 8px;
}

.question-config ::v-deep .el-tree-node__content {
  height: 32px;
}

.question-config ::v-deep .el-tree-node__content:hover {
  background-color: #f5f7fa;
}

.question-config ::v-deep .el-tree--highlight-current .el-tree-node.is-current > .el-tree-node__content {
  background-color: #409eff;
  color: white;
}

/* 表格样式优化 */
.question-config ::v-deep .el-table {
  border: 1px solid #ebeef5;
}

.question-config ::v-deep .el-table__body-wrapper {
  overflow-x: auto;
  overflow-y: auto;
}

.question-config ::v-deep .el-table__header-wrapper {
  overflow: hidden;
}

/* 固定列样式优化 */
.question-config ::v-deep .el-table__fixed-right {
  box-shadow: -1px 0 8px rgba(0, 0, 0, 0.12);
}

/* 表头样式 */
.question-config ::v-deep .el-table th {
  background-color: #f5f7fa !important;
  color: #606266 !important;
  font-weight: 500;
}
</style>